/* General settings card: pure model. Local validation, what changed and the PATCH body. */

export interface GeneralValues {
  name: string;
  description: string;
}

interface SavedProject {
  name: string;
  description?: string | null;
}

export interface GeneralErrors {
  name?: string;
  description?: string;
}

export function generalErrors({ name, description }: GeneralValues): GeneralErrors {
  return {
    name: nameError(name),
    description: description.length > 2000 ? 'Keep the description under 2000 characters' : undefined,
  };
}

function nameError(name: string): string | undefined {
  if (!name.trim()) return 'Name is required';
  return name.length > 120 ? 'Keep the name under 120 characters' : undefined;
}

export const hasGeneralErrors = (e: GeneralErrors): boolean => Boolean(e.name || e.description);

/** An empty description is stored as `null`. */
export const nextDescription = (description: string): string | null => description.trim() || null;

const nameChanged = (v: GeneralValues, p: SavedProject): boolean => v.name.trim() !== p.name;
const descriptionChanged = (v: GeneralValues, p: SavedProject): boolean => nextDescription(v.description) !== (p.description ?? null);

export const generalDirty = (v: GeneralValues, p: SavedProject): boolean => nameChanged(v, p) || descriptionChanged(v, p);

/** Merge patch: only the fields that differ from the saved project. */
export function generalPatch(v: GeneralValues, p: SavedProject): { name?: string; description?: string | null } {
  return { ...(nameChanged(v, p) ? { name: v.name.trim() } : {}), ...(descriptionChanged(v, p) ? { description: nextDescription(v.description) } : {}) };
}

/** The values of the saved project as the form holds them. */
export const savedValues = (p: SavedProject): GeneralValues => ({ name: p.name, description: p.description ?? '' });

/** Another tab or the hub renamed the project: the form follows unless it has unsaved edits (it still shows what was saved before). */
export const followsSaved = (values: GeneralValues, base: GeneralValues): boolean => values.name === base.name && values.description === base.description;

/** A server message wins; the local rule shows only after the first submit. */
export function fieldError(server: Record<string, string>, local: GeneralErrors, submitted: boolean, field: keyof GeneralErrors): string | undefined {
  return server[field] || (submitted ? local[field] : undefined);
}
